import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { API } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Home, Download } from "lucide-react";
import { PLATFORM_ABBR } from "@/lib/brand";

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking");
    const [lookupKey, setLookupKey] = useState(null);

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let tries = 0;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                setLookupKey(data.lookup_key);
                if (data.payment_status === "paid") { setState("paid"); return; }
                if (data.status === "failed" || data.payment_status === "expired") { setState("error"); return; }
            } catch { /* retry */ }
            if (++tries >= 8) { setState("pending"); return; }
            setTimeout(poll, 2000);
        };
        poll();
    }, [sessionId]);

    const isProduct = lookupKey && lookupKey.startsWith("prod_");

    return (
        <Shell>
            {state === "checking" && <><Loader2 className="mx-auto h-12 w-12 animate-spin text-ocean" /><h1 className="mt-6 font-heading text-2xl font-bold text-navy">Confirming your payment…</h1><p className="mt-2 text-slate-600">This only takes a moment.</p></>}
            {state === "paid" && <>
                <CheckCircle2 className="mx-auto h-14 w-14 text-teal" />
                <h1 className="mt-6 font-heading text-3xl font-bold text-navy">Payment confirmed</h1>
                <p className="mt-3 text-slate-600">{isProduct ? "Your digital product is verified and ready to download below." : "Thank you. Our team will follow up with your next steps and a receipt by email."}</p>
                {isProduct && <a href={`${API}/downloads/${sessionId}`} data-testid="download-product-btn"><Button className="mt-6 w-full bg-ocean font-semibold text-white hover:bg-ocean-dark"><Download className="mr-2 h-4 w-4" /> Download your files</Button></a>}
            </>}
            {state === "pending" && <><Loader2 className="mx-auto h-12 w-12 animate-spin text-ocean" /><h1 className="mt-6 font-heading text-2xl font-bold text-navy">Payment processing</h1><p className="mt-3 text-slate-600">Your payment is still processing. You'll get a confirmation email once it clears — access is granted only after verified payment.</p></>}
            {state === "error" && <><XCircle className="mx-auto h-14 w-14 text-red-500" /><h1 className="mt-6 font-heading text-2xl font-bold text-navy">We couldn't confirm your payment</h1><p className="mt-3 text-slate-600">If you were charged, contact us and we'll resolve it right away.</p></>}
            <Button onClick={() => navigate("/")} data-testid="payment-home-btn" variant="outline" className="mt-4 border-slate-300 font-semibold text-navy hover:bg-slate-100"><Home className="mr-2 h-4 w-4" /> Back to home</Button>
        </Shell>
    );
}

export function PaymentCancel() {
    const navigate = useNavigate();
    return (
        <Shell>
            <XCircle className="mx-auto h-14 w-14 text-slate-400" />
            <h1 className="mt-6 font-heading text-3xl font-bold text-navy">Checkout cancelled</h1>
            <p className="mt-3 text-slate-600">No charge was made. Whenever you're ready, we're here to help.</p>
            <Button onClick={() => navigate("/")} data-testid="payment-home-btn" className="mt-8 bg-navy font-semibold text-white hover:bg-navy-light"><Home className="mr-2 h-4 w-4" /> Back to home</Button>
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 text-center">
            <div className="flex w-full max-w-md flex-col si-card p-10">
                <div className="mx-auto mb-6 flex h-11 w-11 items-center justify-center rounded-lg bg-navy font-heading text-sm font-extrabold text-white">{PLATFORM_ABBR}</div>
                {children}
            </div>
        </div>
    );
}
